import * as React from 'react'
import { Check, ChevronsUpDown } from 'lucide-react'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import { ALL_COUNTRIES, PINNED_COUNTRIES } from '@/lib/countries'

// Shipping Country picker for the vendor table. NOTE the backing field is
// still named `country_of_origin` in the DB, schema, and API — only the
// user-facing label was renamed. Origin (where goods are made, which drives
// duty) and ship-from country are not the same thing; if duty logic is ever
// keyed off this column, settle which one it holds first.
// Same Popover + cmdk shape
// as LocationCombobox (typeahead filtering comes free from cmdk), with two
// differences driven by the 190-entry list:
//   1. Common origins are pinned above a separator so United States is a
//      click rather than a scroll.
//   2. The trigger is a bare table-cell-styled button, not the outline
//      Button, so the column reads like the other inline-editable cells.

// Pinned entries are removed from the main list — cmdk keys items by
// `value`, so rendering a country twice makes the check mark and keyboard
// highlight ambiguous.
const PINNED = new Set<string>(PINNED_COUNTRIES)
const REST = ALL_COUNTRIES.filter((c) => !PINNED.has(c))

// The popover portals to <body>, outside the page's .brand-theme wrapper, so
// warm surfaces are set explicitly. Highlight is a neutral warm tint — not
// burgundy (identity) and not amber (attention).
const ITEM_CLS =
  "text-[var(--text-primary)] data-[selected='true']:bg-[var(--surface-header)] data-[selected='true']:text-[var(--text-primary)]"

interface Props {
  value: string | null
  onChange: (value: string | null) => void
  vendor: string
  className?: string
}

export function CountryCombobox({ value, onChange, vendor, className }: Props) {
  const [open, setOpen] = React.useState(false)

  function select(next: string | null) {
    onChange(next)
    setOpen(false)
  }

  // A country already stored but absent from the list (hand-edited SQL, or
  // a name we later renamed) still needs to render and show its check.
  const orphan = value && !PINNED.has(value) && !REST.includes(value as never) ? value : null

  const renderItem = (c: string) => (
    <CommandItem key={c} value={c} onSelect={() => select(c)} className={ITEM_CLS}>
      <Check className={cn('mr-2 h-3.5 w-3.5', value === c ? 'opacity-100' : 'opacity-0')} />
      {c}
    </CommandItem>
  )

  return (
    <Popover open={open} onOpenChange={setOpen} modal>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          aria-label={`Shipping country for ${vendor}`}
          className={cn('flex items-center justify-between gap-1', className)}
        >
          <span className={cn('truncate', !value && 'text-[var(--mute)]')}>{value || '—'}</span>
          <ChevronsUpDown className="h-3 w-3 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[240px] p-0 border-[var(--line-warm)] bg-[var(--surface-card)] text-[var(--text-primary)]"
        align="start"
      >
        <Command className="bg-[var(--surface-card)] text-[var(--text-primary)] [&_[cmdk-input-wrapper]]:border-[var(--line-warm)]">
          <CommandInput placeholder="Search countries…" className="h-8 text-xs text-[var(--text-primary)]" />
          <CommandList>
            <CommandEmpty>No country found.</CommandEmpty>
            <CommandGroup>
              <CommandItem value="__none__" onSelect={() => select(null)} className={ITEM_CLS}>
                <Check className={cn('mr-2 h-3.5 w-3.5', !value ? 'opacity-100' : 'opacity-0')} />
                <span className="text-[var(--mute)]">— None —</span>
              </CommandItem>
              {orphan && renderItem(orphan)}
            </CommandGroup>
            <CommandSeparator className="bg-[var(--line-warm)]" />
            <CommandGroup heading="Common">{PINNED_COUNTRIES.map(renderItem)}</CommandGroup>
            <CommandSeparator className="bg-[var(--line-warm)]" />
            <CommandGroup heading="All countries">{REST.map(renderItem)}</CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
